<template>
  <q-page class="q-pa-sm">
    <breadcrumbs />
    <div>
      <q-input
        v-model="searchTerms"
        class="q-mt-sm"
        dense
        label="Search developer items..."
        input-class="allow-global-search"
        autofocus
      />
      <transition-group name="list-complete">
        <menu-item-category
          v-for="category in filteredList"
          :key="category.label"
          :label="category.label"
        >
          <menu-item-card
            v-for="item in category.entries"
            :key="item.label"
            :item="item"
            default-icon="insert_chart_outlined"
            @click="selectItem(item)"
          />
        </menu-item-category>
      </transition-group>
    </div>
    <create-circuit-dialog v-model="showCreateCircuitDialog" />
    <create-site-dialog v-model="showCreateSiteDialog" />
    <create-library-site-dialog v-model="showCreateLibrarySiteDialog" />
    <developer-advanced-functions-dialog v-model="showAdvancedFunctionsDialog" />
  </q-page>
</template>

<script>
import { serverUri } from 'src/config'
import CreateCircuitDialog from 'src/modules/admin/circuit/CreateCircuitDialog'
import Breadcrumbs from 'src/modules/admin/components/Breadcrumbs'
import DeveloperAdvancedFunctionsDialog from 'src/modules/admin/developer/DeveloperAdvancedFunctionsDialog'
import AdminMenuMixin from 'src/modules/admin/menu/AdminMenuMixin'
import MenuItemCard from 'src/modules/admin/menu/MenuItemCard'
import MenuItemCategory from 'src/modules/admin/menu/MenuItemCategory'
import CreateLibrarySiteDialog from 'src/modules/admin/site/CreateLibrarySiteDialog'
import CreateSiteDialog from 'src/modules/admin/site/CreateSiteDialog'

/**
 * @typedef {import("./AdminMenuMixin").MenuItem} MenuItem
 * @type {MenuItem[]}
 */
export const developersMenuItems = [{
  label: 'Infrastructure',
  entries: [
    {
      indent: 1,
      route: 'client-versions',
      label: 'Client Versions',
      icon: 'new_releases',
      description: 'List of Client Versions and Release Notes'
    },
    {
      indent: 1,
      route: 'netlify-status',
      label: 'Netlify Site Deploy Status',
      icon: 'insert_chart_outlined',
      description: 'Netlify deploy status for all sites'
    },
    {
      indent: 1,
      method: 'openSidekiq',
      label: 'Job Server Status',
      icon: 'settings',
      description: 'Launch the Sidekiq job server status page'
    },
    {
      indent: 1,
      route: 'job-logs',
      label: 'Logging Service',
      description: 'View and Search through application logs',
      icon: 'table_view'
    }
  ]
},
{
  label: 'External Applications',
  entries: [
    {
      indent: 1,
      route: 'oauth-applications',
      label: 'External Ticketing Applications',
      icon: 'rss_feed',
      description: 'External Ticketing Provider List'
    }
  ]
},
{
  label: 'Settings',
  entries: [
    {
      indent: 1,
      route: 'site-settings',
      label: 'Developer Site Settings',
      icon: 'account_balance',
      description: 'Settings that apply to the Site'
    },
    {
      indent: 1,
      route: 'circuit-settings',
      label: 'Developer Circuit Settings',
      icon: 'account_balance',
      description: 'Settings that apply to the Circuit'
    },
    {
      indent: 1,
      route: 'feature-flags',
      label: 'Feature Flags',
      icon: 'tune',
      description: 'List status of feature flags for current Site'
    },
    {
      indent: 1,
      route: 'time-slots',
      label: 'Time Slots',
      icon: 'calendar_view_week',
      description: 'How you conceptually define times when a block of shows play (e.g. noon, matinee, evening, night, etc.)'
    },
    // TODO: Eventually expose this to clients under Settings
    {
      indent: 1,
      route: 'analytics-events',
      label: 'Analytics Events (Alpha)',
      icon: 'analytics',
      description: 'Tracked analytics events reference'
    },
    {
      indent: 1,
      route: 'developer-payment-connectors',
      label: 'Global Payment Connector Settings',
      icon: 'credit_card',
      description: 'Settings that apply to the Region'
    }
  ]
},
{
  label: 'Circuit and Site Creation',
  entries: [
    {
      indent: 1,
      label: 'Create New Circuit',
      icon: 'playlist_add',
      description: 'Create a new circuit, either blank or starting with a template',
      method: 'showCreateCircuit'
    },
    {
      indent: 1,
      label: 'Create New Site',
      icon: 'playlist_add',
      description: 'Add a new site to this circuit, either blank or starting by copying an existing site',
      method: 'showCreateSite'
    },
    {
      indent: 1,
      label: 'Create Library Site',
      icon: 'menu_book',
      description: 'Add a library to this circuit to share movies/items/etc between sites',
      method: 'showCreateLibrarySite'
    }
  ]
},
{
  label: 'Advanced',
  entries: [
    {
      indent: 1,
      label: 'Execute Advanced Action',
      icon: 'build',
      description: 'Execute an action on the server',
      method: 'showAdvancedFunctions'
    },
    {
      indent: 1,
      label: 'Unknown Payments',
      icon: 'gavel',
      description: 'Stripe payments of unknown origin',
      route: 'unknown-payments'
    },
    {
      indent: 1,
      label: 'Release Candidates',
      icon: 'travel_explore',
      description: 'Upcoming Features and Fixes',
      route: 'release-candidates'
    },
    {
      indent: 1,
      label: 'Data Migrations',
      icon: 'developer_board',
      description: 'View migration status',
      route: 'data-migrations'
    }
  ]
},
{
  label: 'Imports',
  entries: [
    {
      indent: 1,
      label: 'Data Imports',
      icon: 'move_to_inbox',
      description: 'Import Users, Vouchers and Gift Cards.',
      route: 'cinema-data-migrations'
    }
  ]
}]

export default {
  name: 'DevelopersMenu',
  components: {
    Breadcrumbs,
    CreateCircuitDialog,
    CreateSiteDialog,
    CreateLibrarySiteDialog,
    DeveloperAdvancedFunctionsDialog,
    MenuItemCard,
    MenuItemCategory
  },
  mixins: [AdminMenuMixin],
  data () {
    return {
      showCreateCircuitDialog: false,
      showCreateSiteDialog: false,
      showCreateLibrarySiteDialog: false,
      showAdvancedFunctionsDialog: false
    }
  },
  beforeCreate () {
    // Required by AdminMenuMixin, before immediate watcher
    this.menuItems = developersMenuItems
    this.basePath = 'developers'
  },
  methods: {
    trackSelectItem (item) {
      this.$trackEvent(this.$indyUserEvents.DEVELOPER_ITEM_SELECTED, { item })
    },
    openSidekiq () {
      window.open(serverUri + '/sidekiq', '_blank')
    },
    showCreateCircuit () {
      this.showCreateCircuitDialog = true
    },
    showCreateSite () {
      this.showCreateSiteDialog = true
    },
    showCreateLibrarySite () {
      this.showCreateLibrarySiteDialog = true
    },
    showAdvancedFunctions () {
      this.showAdvancedFunctionsDialog = true
    }
  },
  trackPage: true
}
</script>
